/**
 * GlobalComposerHost — mounted once at the root. Reads the `composer` redux
 * slice and renders the ComposerSheet. Any screen can `dispatch(openComposer(
 * { projectId, inboxEmail, initial }))` to open the composer without owning
 * the sheet UI itself.
 *
 * Half of the "inbox useless, compose prominent" delivery: the compose
 * surface is no longer trapped inside InboxScreen.
 */

import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useTheme } from '../hooks/useTheme';
import { showNotification } from '../store/actions/auth';
import ComposerSheet from './ComposerSheet';
import { closeComposer, markComposerSent, type ComposerState } from '../store/reducers/composer';

export default function GlobalComposerHost() {
  const emailRequestsEnabled = useFeatureFlag('plan_email_requests_enabled', false);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const composer = useSelector((s: any) => s?.composer as ComposerState | undefined);

  const handleClose = useCallback(() => {
    dispatch(closeComposer());
  }, [dispatch]);

  const handleSent = useCallback(() => {
    dispatch(markComposerSent());
    if (composer?.initial.emailUpdateRequest) dispatch(showNotification('success', 'Request sent', 'Watch the plan inbox for confirmation or next steps from the organization.'));
  }, [dispatch, composer?.initial.emailUpdateRequest]);

  if (composer?.initial.emailUpdateRequest && !emailRequestsEnabled) return null;
  if (!composer?.open || composer.projectId == null) return null;

  return (
    <ComposerSheet
      visible={composer.open}
      initial={composer.initial}
      inboxEmail={composer.inboxEmail}
      projectId={composer.projectId}
      onClose={handleClose}
      onSent={handleSent}
      theme={theme}
    />
  );
}
